// src/types/Product.ts
export interface Product {
  id?: string;
  name: string;
  description: string;
  price: number;
  discount?: number; // Discount percentage (0-100)
  unitCost?: number;
  isOutOfStock: boolean;
  categoryId: string;
  subcategoryId?: string;
  brandId?: string; // Optional brand association
  sku: string;
  images: string[];
  variants: ProductVariant[];
  hasVariants: boolean;
  // Featured product flag
  isFeatured: boolean;
  // Stock management fields (total across all warehouses)
  stockQuantity: number;
  minStockLevel: number;
  maxStockLevel: number;
  isLowStock: boolean;
  // Warehouse allocations for simple products (stored in product document)
  warehouseAllocations?: WarehouseAllocation[];
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string;
}

export interface ProductFormData {
  name: string;
  description: string;
  price: string;
  discount: string; // Discount percentage as string for form input
  unitCost: string;
  isOutOfStock: boolean;
  isFeatured?: boolean;
  categoryId: string;
  subcategoryId: string;
  brandId: string; // Optional brand association (empty string = none)
  sku: string;
  images: File[];
  variants: ProductVariant[];
  hasVariants: boolean;
  // Stock management fields
  stockQuantity: string;
  minStockLevel: string;
  maxStockLevel: string;
  // Warehouse allocations (for UI only, not stored in product)
  warehouseAllocations: WarehouseAllocation[];
}

// Warehouse Allocation Interface (for form data)
export interface WarehouseAllocation {
  warehouseId: string;
  warehouseName?: string;
  quantity: number;
  minStockLevel: number;
  maxStockLevel: number;
  isLowStock: boolean;
  isOutOfStock: boolean;
  isOverStock: boolean;
}

// Warehouse Inventory Record (stored separately)
export interface WarehouseInventory {
  id?: string;
  productId: string;
  productName?: string;
  variantId?: string;
  variantName?: string;
  quantity: number;
  minStockLevel: number;
  maxStockLevel: number;
  isLowStock: boolean;
  isOutOfStock: boolean;
  isOverStock: boolean;
  lastUpdated?: Date;
  sku?: string;
  price?: number;
  discount?: number; // Discount percentage
}

// Category Management Types
export interface Category {
  id?: string;
  name: string;
  description?: string;
  parentId?: string;
  imageUrl?: string | null;
  isActive: boolean;
  sortOrder?: number;
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string;
  isStarred?: boolean;
}

export interface CategoryFormData {
  name: string;
  description: string;
  parentId: string;
  isActive: boolean;
  sortOrder: string;
}

// Variant Management Types
export interface VariantOption {
  id?: string;
  name: string;
  value?: string;
  isActive: boolean;
}

export interface VariantType {
  id?: string;
  name: string;
  isRequired: boolean;
  allowsMultiple: boolean;
  options: VariantOption[];
  isActive: boolean;
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string;
}

export interface VariantSelection {
  variantType: string;
  variantValue: string;
}

export interface ProductVariant {
  id?: string;
  productId?: string;
  variantSelections: VariantSelection[];
  price: number;
  discount?: number; // Discount percentage (0-100)
  unitCost?: number;
  sku: string;
  isDefault?: boolean;
  // Stock management for variants (total across all warehouses)
  stockQuantity: number;
  minStockLevel: number;
  maxStockLevel: number;
  isLowStock: boolean;
  isOutOfStock: boolean;
  isOverStock: boolean;
  // Warehouse allocations (for UI only)
  warehouseAllocations?: WarehouseAllocation[];
}

export interface VariantTypeFormData {
  name: string;
  isActive: boolean;
  options: VariantOption[];
}

// Utility interfaces
export interface CategoryWithSubcategories extends Category {
  subcategories: Category[];
}

export interface ProductWithDetails extends Product {
  category?: Category;
  subcategory?: Category;
  variantDetails?: {
    variantSelections: VariantSelection[];
    price: number;
    discount?: number; // Discount percentage
    sku: string;
    isDefault?: boolean;
    stockQuantity: number;
    minStockLevel: number;
    maxStockLevel: number;
    warehouseAllocations?: WarehouseAllocation[];
    isLowStock: boolean;
    isOutOfStock: boolean;
  }[];
  warehouseInventories?: WarehouseInventory[];
}

// Global configuration for variants
export const VARIANT_CONFIG = {
  maxVariantSelections: 2,
} as const;

// Import Warehouse types
export interface Warehouse {
  id?: string;
  name: string;
  code: string;
  description: string;
  address: WarehouseAddress;
  contact: WarehouseContact;
  capacity: WarehouseCapacity;
  isActive: boolean;
  operatingHours?: WarehouseOperatingHours;
  createdAt?: Date;
  updatedAt?: Date;
  createdBy?: string;
  createdByName?: string;
  updatedBy?: string;
  updatedByName?: string;
  // Statistics that will be updated
  stats?: WarehouseStats;
}

export interface WarehouseAddress {
  street: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
}

export interface WarehouseContact {
  phone: string;
  email: string;
  manager: string;
}

export interface WarehouseCapacity {
  total: number;
  used: number;
  available: number;
}

export interface WarehouseOperatingHours {
  open: string;
  close: string;
  timezone: string;
}

export interface WarehouseStats {
  productCount: number;
  variantCount: number;
  totalStock: number;
  lowStockCount: number;
  outOfStockCount: number;
}

export interface WarehouseFormData {
  name: string;
  code: string;
  description: string;
  address: WarehouseAddress;
  contact: WarehouseContact;
  capacity: {
    total: string;
    used: string;
  };
  isActive: boolean;
  operatingHours: WarehouseOperatingHours;
}

export interface WarehouseDashboardStats {
  total: number;
  active: number;
  inactive: number;
  totalCapacity: number;
  usedCapacity: number;
  availableCapacity: number;
  utilizationRate: number;
}

// Warehouse with inventory
export interface WarehouseWithInventory extends Warehouse {
  inventory: WarehouseInventory[];
}

// Utility function to calculate discounted price
export const calculateDiscountedPrice = (price: number, discount?: number): number => {
  if (!discount || discount <= 0 || discount > 100) {
    return price;
  }
  return price - (price * discount / 100);
};

// Utility function to calculate discount amount
export const calculateDiscountAmount = (price: number, discount?: number): number => {
  if (!discount || discount <= 0 || discount > 100) {
    return 0;
  }
  return price * discount / 100;
};